/* hs-eslint ignored failing-rules */
/* eslint-disable prefer-promise-reject-errors */

import enviro from 'enviro';
import { fromJS } from 'immutable';
import { useCallback, useMemo } from 'react';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { AsyncStatus } from 'reporting-data/asyncData/AsyncStatus';
import { useAsyncFetcher } from 'reporting-data/asyncData/useAsyncFetcher';
import { post } from 'reporting-data/request/http';
import { isJourneyReport } from 'reporting-snowflake/funnel/types/journeyReport';
import { isBaseReport, isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { getDisplayParamsColumns, getDisplayParams as getConfigDisplayParams } from 'reporting-data/report/configReportGetters';

//Expects JS Object

const REPORT_MIGRATION_URL = 'reporting-migration/v2/reports/migrate';
const REPORT_REVERSIBLE_MIGRATION_URL = `${REPORT_MIGRATION_URL}?onlyReversible=true`;
const rewriteLocalReportMigrationUrl = url => {
  return `https://local.hubspot${enviro.isQa() ? 'qa' : ''}.com/${url}`;
};
function objectFunctionFilter(jsObject) {
  if (!jsObject || typeof jsObject !== 'object' || Array.isArray(jsObject)) {
    return undefined;
  }
  const filteredObject = {};
  let key;
  for (key in jsObject) {
    if (!Object.prototype.hasOwnProperty.call(jsObject, key)) {
      continue;
    }
    const value = jsObject[key];
    if (typeof value === 'object' && !Array.isArray(value)) {
      filteredObject[key] = objectFunctionFilter(value);
    } else if (typeof value === 'function') {
      filteredObject[key] = value;
    }
  }
  return filteredObject;
}
const getMigrationUrl = (useLocalReportMigrationApi, onlyRunReversibleMigrations) => {
  const baseMigrationUrl = onlyRunReversibleMigrations ? REPORT_REVERSIBLE_MIGRATION_URL : REPORT_MIGRATION_URL;
  return useLocalReportMigrationApi ? rewriteLocalReportMigrationUrl(baseMigrationUrl) : baseMigrationUrl;
};

// The idea behind this is that only active migrations that need to be run as FE report config/report definition transforms
//  would have their names added here. After a migration is completed then they could removed via PR in the future.

// THIS SHOULD ONLY BE USED IN THE REPORTING SANDBOX AND REPORT BUILDER
export const migrateReport = (report, useLocalReportMigrationApi = false, onlyRunReversibleMigrations = false) => {
  // TODO: Delete this and have the sandbox use the public hook
  if (isJourneyReport(report.toJS())) {
    return Promise.reject();
  }
  const migrationUrl = getMigrationUrl(useLocalReportMigrationApi, onlyRunReversibleMigrations);
  return post(migrationUrl, {
    data: [report]
  }).then(migratedReports => migratedReports.first());
};
const resultFormatter = result => result.first();
export const useMigratedReport = ({
  asyncReport,
  deferred = false,
  useLocalReportMigrationApi = false,
  onlyRunReversibleMigrations = false
}) => {
  const report = isSucceeded(asyncReport) ? asyncReport.data : undefined;
  const isReportPending = isLoading(asyncReport);
  const requestArgs = useMemo(() => {
    return {
      reportForFetch: report
    };
  }, [report]);
  const fetcher = useCallback(({
    reportForFetch
  }) => {
    const migrationUrl = getMigrationUrl(useLocalReportMigrationApi, onlyRunReversibleMigrations);
    return post(migrationUrl, {
      data: [reportForFetch]
    });
  }, [useLocalReportMigrationApi, onlyRunReversibleMigrations]);
  const asyncData = useAsyncFetcher({
    fetcher,
    requestArgs,
    deferred: deferred || isReportPending,
    resultFormatter
  });
  const result = useMemo(() => {
    if (isReportPending) {
      return asyncReport;
    }
    if (deferred && report) {
      // In case when no migration is needed or journeyReport, return the report as is
      return {
        data: report,
        status: AsyncStatus.SUCCEEDED
      };
    }
    if (isLoading(asyncData)) {
      return asyncData;
    }
    if (isFailed(asyncData)) {
      return asyncData;
    }
    const migratedReport = isSucceeded(asyncData) ? asyncData.data : undefined;
    const displayParams = report && isBaseReport(report) && report.get('displayParams');
    if (displayParams && migratedReport && isBaseReport(migratedReport)) {
      // for "columns" property, if the columns is an empty object, we are going to overwrite this with the migration result
      // else, we are going to merge the migration result with the existing columns
      // because possibiliy for columns property that has value, there is a chance that it will be a callback function, which is not handled in the migration result

      const maybeUpdatedDisplayParams = isConfigReport(report) ? getConfigDisplayParams(report).set('columns', getDisplayParamsColumns(report, {
        excludeEmpty: true
      })) : displayParams;
      const reportWithDisplayParams = migratedReport.set('displayParams', migratedReport.get('displayParams').mergeDeep(
      // Display params allow app to define override for a DataWell report's header/body
      // rendering.  The following logic maintains these overrides.
      fromJS(objectFunctionFilter(maybeUpdatedDisplayParams.toJS()))));
      return {
        data: reportWithDisplayParams,
        status: AsyncStatus.SUCCEEDED
      };
    }
    return asyncData;
  }, [asyncData, asyncReport, report, deferred, isReportPending]);
  return result;
};